import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Text,
  View,
  ScrollView,
  Image,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import Box from '@/Atoms/Box';
import CCButton from '@/Atoms/CCButton';
import {FontSizes} from '@/Utils/Dimensions';
import Spacer from '@/Atoms/Spacer';
import {useNavigation, useRoute} from '@react-navigation/native';
import {tap} from 'rxjs/operators';
import {
  isGameAlreadyAnalyzed,
  type BrowserAnalysisMode,
} from './browserGameAnalysis';
import {
  getBackgroundGameAnalysis,
  stopBackgroundGameAnalysis,
} from './backgroundAnalysisManager';
import {accuracy} from './analysisParity';
import styles from './styles';
import ChessboardComponent from 'chessboard-package';
import WinningBar from './component/WinningBar';
import AnalysisRightComponent from './component/AnalysisRightComponent';
import {
  calculateErrors,
  calculateErrorsByColor,
  calculateOpeningPrinciplesByColor,
  calculateWinningChances,
  convertTimeControlToMins,
  evaluateMove,
} from './utils';
import useVerifyPuzzlesApi from '@/Hooks/PuzzlesApi/verifyPuzzlesApi';
import ProgressBar from '@/Atoms/ProgressBar';
import {Chess} from 'chess.js';
import CCText from '@/Atoms/CCText';
import blunderIcon from '../../Assets/Images/Svgs/learning/blunder-icon.svg';
import mistakesIcon from '../../Assets/Images/Svgs/learning/mistakes-icon.svg';
import inaccurateIcon from '../../Assets/Images/Svgs/learning/inaccuracy-icon.svg';
import CCColors from '@/Utils/CCColors';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import SlideModal from '@/Atoms/SlideModal';
import {ScreenName} from '@/navigators/StackConstants';
import Loader from '@/Atoms/CCLoader/CCLoader';
import {logEvent} from '@/Utils/GAEvents';
import useGAPageView from '@/navigators/useGAPageView';
import GameScoreTable from './component/GameScoreTable';
import {STARTING_FEN, capitalizeFirstLetter} from '@/Utils/CommonUtils';
import wrongSound from '../../Assets/Sounds/wrong.mp3';
import useSound from 'use-sound';
import SuccessModal from './component/SuccessModal';
import useSaveAlternateMoves from '@/Hooks/SaveAlternateMovesApi/useSaveAlternateMoves';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import BottomSheet from '@/Atoms/BottomSheet';
import ConditionalRender from '@/Atoms/ConditionalRender';
import SafeAreaWrapper from '@/Atoms/SafeViewWrapper';
import useAnimation from '@/Hooks/useAnimation';
import Animated from 'react-native-reanimated';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';
import {useNonPersistedUserPlanStore} from '@/Store/userPlanStore';
import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import Ionicons from 'react-native-vector-icons/Ionicons';
import useDownloadGameReviewPGN from '@/Hooks/DownloadGameReviewPGNApi/useDownloadGameReviewPGN';
import useGetOpeningModelGameApi from '@/Hooks/AssessmentApi/useGetOpeningModelGameApi';
import Svg, {Circle, Line, Polygon, Polyline} from 'react-native-svg';

// ─── Constants ────────────────────────────────────────────────────────────────
const OPENING_PRINCIPLE_SIDEBAR_TEAL = '#3DAB9E';
const OPENING_PRINCIPLE_SIDEBAR_RED = CCColors.Gradient.LightRed;
const REVIEW_LEFT_PANEL_WIDTH = 324;
const REVIEW_RIGHT_PANEL_WIDTH = 360;
const REVIEW_CONTAINER_HORIZONTAL_SPACE = 64;
const REVIEW_BOARD_SIDE_GAP = 28;
const REVIEW_BOARD_VERTICAL_RESERVE = 220;
const REVIEW_MIN_DESKTOP_BOARD_SIZE = 280;
const REVIEW_COMPACT_WIDTH =
  REVIEW_LEFT_PANEL_WIDTH +
  REVIEW_RIGHT_PANEL_WIDTH +
  REVIEW_CONTAINER_HORIZONTAL_SPACE +
  REVIEW_BOARD_SIDE_GAP +
  REVIEW_MIN_DESKTOP_BOARD_SIZE;

// ─── Mate score sentinel (matches server convention) ──────────────────────────
const MATE_SCORE = 90000;
const MATE_THRESHOLD = MATE_SCORE - 1000; // e.g., anything >= 89000 is mate

// ─── Pure helpers (defined outside component so they are stable references) ───

const isRenderableGameMove = (move: any) =>
  Boolean(
    move &&
      typeof move === 'object' &&
      String(move?.move || '').trim() &&
      String(move?.fen || '').trim(),
  );

const renderableGameMoves = (moves: any) =>
  Array.isArray(moves) ? moves.filter(isRenderableGameMove) : [];

/**
 * Normalises an arbitrary value to 'white' | 'black' | null.
 * Accepts full words, single letters, and any casing.
 */
const normalizeBoardColor = (value: unknown): 'white' | 'black' | null => {
  const color = String(value ?? '')
    .toLowerCase()
    .trim();
  if (color === 'white' || color === 'w') {
    return 'white';
  }
  if (color === 'black' || color === 'b') {
    return 'black';
  }
  return null;
};

/**
 * Converts a raw engine score (centipawns from the USER's perspective) into
 * the WHITE perspective used everywhere in the graph/display layer.
 */
const scoreFromUserToWhitePerspective = (
  score: unknown,
  _userColor: unknown, // intentionally unused — scores are already white-perspective
): number | null => {
  if (
    score === null ||
    score === undefined ||
    score === '' ||
    score === false
  ) {
    return null;
  }

  // Handle mate-string formats
  if (typeof score === 'string') {
    const mateMatch = score.match(/^([+-]?)(?:M|#)(\d+)$/i);
    if (mateMatch) {
      const sign = mateMatch[1] === '-' ? -1 : 1;
      const mateIn = parseInt(mateMatch[2], 10);
      return sign * (MATE_SCORE - mateIn);
    }
  }

  const numeric = Number(score);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  // Score is already in white's perspective — return as-is
  return numeric;
};

const firstFiniteNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') {
      continue;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

// ─── Small presentational sub-components ─────────────────────────────────────

function OpeningPrincipleOChip({tint}: {tint: string}) {
  const size = 18;
  return (
    <View
      style={{
        width: 25,
        height: 20,
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: tint,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <CCText
          style={{
            color: '#FFFFFF',
            fontSize: Math.round(size * 0.42),
            fontWeight: '900',
            lineHeight: Math.round(size * 0.9),
          }}>
          O
        </CCText>
      </View>
    </View>
  );
}

function extractMoves(chessNotation: string): string[] {
  const tokens = chessNotation?.split(' ');
  const moves = tokens.filter(token => !token.endsWith('.'));
  if (moves?.[0]?.includes('...')) {
    const resultMoves = moves?.[0]?.split('...')?.[1];
    return [resultMoves, ...moves.splice(1)];
  }
  return moves;
}

// ─── Main component ───────────────────────────────────────────────────────────

function ChessAnalysis({setUserData, setStreakCount, setStreakDetails}) {
  const route = useRoute();
  const {width: viewportWidth, height: viewportHeight} = useWindowDimensions();
  const {isMobile, isTablet} = useMediaQuery();
  const isMobileOrTab = useMemo(
    () => isMobile || isTablet || viewportWidth < REVIEW_COMPACT_WIDTH,
    [isMobile, isTablet, viewportWidth],
  );
  const isCompactDesktopLayout = useMemo(
    () => !isMobile && !isTablet && viewportWidth < REVIEW_COMPACT_WIDTH,
    [isMobile, isTablet, viewportWidth],
  );
  const chessboardColorTheme = useChessboardColorTheme();

  // single navigation reference — removed duplicate `navigate` hook
  const navigation = useNavigation();

  const gameId = route.params?.game_id;
  const analyzedParam = Number(
    route.params?.analyzed ?? route.params?.alreadyAnalyzed ?? 0,
  );
  const alreadyAnalyzed = analyzedParam === 1 || analyzedParam === 2;
  const [backendAlreadyAnalyzed, setBackendAlreadyAnalyzed] =
    useState(alreadyAnalyzed);
  const [analysisMetadataChecked, setAnalysisMetadataChecked] =
    useState(alreadyAnalyzed);
  const effectiveAlreadyAnalyzed = alreadyAnalyzed || backendAlreadyAnalyzed;
  const modelGameId = route.params?.model_game_id;
  const isModelGameView = Boolean(route.params?.viewgame && modelGameId);
  const timeControl = route.params?.time_control;
  const timeClass = route.params?.time_class;

  const setStreakDetailsSummary = useLoggedInUserStore(
    state => state.setStreakDetailsSummary,
  );
  const [attemptId, setAttemptId] = useState(0);
  const isStaff = route.params?.isUser;
  const opponent = route.params?.opponent;
  const player = route.params?.player;

  // initialise playerColor to 'white' (safe default) instead of ''
  // so that scoreFromUserToWhitePerspective never receives an unknown color
  // before the real value arrives from the server / route params.
  const [playerColor, setPlayerColor] = useState<string>(() => {
    const fromRoute = normalizeBoardColor(
      route?.params?.modelGameColor || route?.params?.playerColor,
    );
    return fromRoute === 'black' ? 'Black' : 'White';
  });

  const viewGameColor = useMemo(() => {
    const raw =
      route?.params?.modelGameColor ||
      route?.params?.playerColor ||
      playerColor ||
      'white';
    return String(raw || 'white').toLowerCase();
  }, [route?.params, playerColor]);

  const opponentRating = route.params?.opponent_rating;
  const playerRating = route.params?.player_rating;
  const [viewgame, setViewgame] = useState(route.params?.viewgame);
  const [analysisMode, setAnalysisMode] =
    useState<BrowserAnalysisMode>('normal');
  const setStreakConfig = useLoggedInUserStore(state => state.setStreakConfig);
  const [isLoading, setIsLoading] = useState(false);
  const [gameOverviewDetails, setGameOverview] = useState(null);
  const [modelGameMeta, setModelGameMeta] = useState<any>(null);
  const {mutate: fetchModelGame} = useGetOpeningModelGameApi();

  useEffect(() => {
    if (!isModelGameView) {
      return;
    }
    fetchModelGame(
      {model_game_id: modelGameId},
      {
        onSuccess: resp => {
          setModelGameMeta(resp?.data || null);
          setPlayerColor('White');
          setGameOverview({
            id: 0,
            userid: 0,
            platform: 'ModelGame',
            url: '',
            color: 'White',
            pgn: resp?.data?.pgn || '',
            time_control: '',
            end_time: '',
            rated: 0,
            uuid: '',
            time_class: '',
            result: resp?.data?.result || '',
            reason: '',
            rating: 0,
            opponent_rating: 0,
            opponent_profile: '',
            opponent_username: resp?.data?.black_player || '',
            opening: resp?.data?.eco || '',
            total_number_of_moves: 0,
            castling_index: 0,
            opponent_castling_index: 0,
            average_time: 0,
            opponent_average_time: 0,
            time_left_on_clock: 0,
            opponent_time_left_on_clock: 0,
            time_utilized: 0,
            opponent_time_utilized: 0,
            total_game_duration_in_minutes: 0,
            accuracy: 0,
            opponent_accuracy: 0,
            analyzed: 0,
            unsolved: 0,
            annotation_reason: 0,
            tournament_id: 0,
            round_id: 0,
            created_time: '',
            updated_time: '',
            username: resp?.data?.white_player || '',
          });
        },
      },
    );
  }, [isModelGameView, modelGameId]);

  function generateNewFen(currentFen, move) {
    try {
      const chess = new Chess(currentFen);
      const result = chess.move(move);
      if (result === null) {
        return currentFen;
      }
      return chess.fen();
    } catch {
      return currentFen;
    }
  }

  const [tabOpen, setTabOpen] = useState('GAME REVIEW');
  const [openSuccessModal, setOpenSuccessModal] = useState({
    flag: false,
    correctSeq: [],
  });
  const [showError, setShowError] = useState(false);

  const [fen, setFen] = useState({fen: STARTING_FEN});
  const [fen2, setFen2] = useState({fen: STARTING_FEN, flag: false});
  const [tableData, setTableData] = useState([]);
  const [isPuzzleScreen, setIsPuzzleScreen] = useState({flag: false, id: ''});

  const [puzzleResult, setPuzzleResult] = useState('None');
  const [puzzleHint, setPuzzleHint] = useState('None');
  const [puzzleDescription, setPuzzleDescription] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [resetChessboard, setResetChessboard] = useState(false);
  const [gameScoreDetails, setGameScoreDetails] = useState(null);
  const [selectedManualMove, setSelectedManualMove] = useState(null);
  const [moveSelectedInReview, setMoveSelectedInReview] = useState('');
  const [manualMoves, setManualMoves] = useState([]);
  const [timelineGraphWidth, setTimelineGraphWidth] = useState(0);
  const [boardColumnWidth, setBoardColumnWidth] = useState(0);
  const latestBoardMoveRef = useRef<any>(null);

  const setUserGems = useLoggedInUserStore(state => state.setGems);
  const setUserLevel = useLoggedInUserStore(state => state.setLevel);
  const [rightcolumnWidth, setrightcolumnWidth] = useState(0);
  const [correspondingFen, setCorrespondingFen] = useState<string>('start');
  const [isFenFromOutside, setIsFenFromOutside] = useState<boolean>(true);
  const {setSubscriptionModalOpen} = useNonPersistedUserPlanStore();

  const onRightColumnLayout = event => {
    const {width} = event.nativeEvent.layout;
    setrightcolumnWidth(width);
  };

  const [engineInfo, setEngineInfo] = useState();
  const [puzzleId, setPuzzleId] = useState(0);
  const [incorrectSound] = useSound(wrongSound);
  const [fullHint, setFullHint] = useState('');
  const [infocolor, setInfocolor] = useState('');
  const [fullHint2, setFullHint2] = useState('');

  const gameDataBoardOrientation = useMemo(
    () =>
      normalizeBoardColor(
        gameOverviewDetails?.color ||
          route?.params?.playerColor ||
          route?.params?.modelGameColor ||
          playerColor,
      ),
    [
      gameOverviewDetails?.color,
      route?.params?.playerColor,
      route?.params?.modelGameColor,
      playerColor,
    ],
  );

  const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>(
    gameDataBoardOrientation || normalizeBoardColor(viewGameColor) || 'white',
  );
  const [hasManualBoardOrientation, setHasManualBoardOrientation] =
    useState(false);

  useEffect(() => {
    if (hasManualBoardOrientation) {
      return;
    }
    if (gameDataBoardOrientation) {
      setBoardOrientation(gameDataBoardOrientation);
      return;
    }
    if (viewgame) {
      const viewOrientation = normalizeBoardColor(viewGameColor);
      if (viewOrientation) {
        setBoardOrientation(viewOrientation);
      }
    }
  }, [
    gameDataBoardOrientation,
    hasManualBoardOrientation,
    viewGameColor,
    viewgame,
  ]);

  useEffect(() => {
    setHasManualBoardOrientation(false);
  }, [gameId, modelGameId]);

  // Keep playerColor in sync with the analysis source of truth.
  useEffect(() => {
    if (!gameDataBoardOrientation) {
      return;
    }
    const normalized = gameDataBoardOrientation === 'white' ? 'White' : 'Black';
    setPlayerColor(prev => (prev === normalized ? prev : normalized));
  }, [gameDataBoardOrientation]);

  const topPlayerType = boardOrientation === 'white' ? 'black' : 'white';
  const bottomPlayerType = boardOrientation;

  const handleSelectMoveInReview = useCallback(
    (tempMove: any) => {
      if (tempMove?.alternate_moves && !manualMoves?.[tempMove?.fen]) {
        setManualMoves(prev => ({
          ...prev,
          [tempMove?.fen]: tempMove?.alternate_moves?.map(i => ({
            ...i,
            newFen: i?.fen,
            lastFen: i?.previous_fen,
          })),
        }));
      }
      setMoveSelectedInReview(tempMove);
    },
    [manualMoves],
  );

  const handleSelectedTabChecks = (tab: string) => setTabOpen(tab);

  const handleOnSuccessfulVerification = async data => {
    setIsLoading(false);
    if (data?.is_permitted === false) {
      setSubscriptionModalOpen(true);
      incorrectSound();
      setPuzzleResult('False');
      handleFen(fen);
      setTimeout(() => {
        setPuzzleResult('None');
        setPuzzleHint('None');
      }, 10000);
      setResetChessboard(prev => !prev);
    } else if (data?.success) {
      setStreakDetailsSummary?.(data?.streak_details_summary);
      setStreakConfig?.(data?.streak_details);
      setRefresh(!refresh);
      if (data?.points) {
        useLoggedInUserStore.setState({
          gems: data?.points.points,
          pointsToNextLevel: data?.points.points_to_next_level,
          level: data?.points.level,
          netScore: data?.net_score,
        });
      }
      setUserData(prevData => ({...prevData, net_score: data?.net_score}));
      setStreakDetails?.(data?.streak_details);
      setStreakCount?.(data?.streak_details_summary);
      setPuzzleResult('True');
      setPuzzleDescription('Correct Answer!');
      setFullHint('');
      setFullHint2('');
      if (data?.hint_response) {
        setFullHint(`Hurray that's the correct move! ${data?.hint_response}`);
      }
      if (data?.hint_response2) {
        setFullHint2(`Hurray that's the correct move! ${data?.hint_response2}`);
      }
      setAttemptId(data?.attempt_id);
      setInfocolor(CCColors.Green);
      const solvedMove = latestBoardMoveRef.current || selectedManualMove;
      if (solvedMove?.newFen) {
        handleFen({
          ...fen,
          fen: solvedMove.newFen,
          ...solvedMove,
        });
      }
      setTimeout(() => {
        setPuzzleResult('None');
        setPuzzleHint('None');
      }, 10000);
    } else {
      incorrectSound();
      setAttemptId(data?.attempt_id);
      setPuzzleResult('False');
      setFullHint('');
      setFullHint2('');
      if (data?.hint) {
        setPuzzleHint(data?.hint);
        setPuzzleDescription(
          data?.resp_move
            ? 'Incorrect! Opponent will respond with ' +
                data?.resp_move +
                '. Try moving the ' +
                data?.hint +
                '.'
            : 'Incorrect Move!',
        );
        if (data?.hint_response) {
          setPuzzleDescription(data?.hint_response);
          setFullHint(`Incorrect move! ${data?.hint_response}`);
          setInfocolor(CCColors.Red);
        }
        if (data?.hint_response2) {
          setFullHint2(`Incorrect move! ${data?.hint_response2}`);
        }
      } else {
        setPuzzleDescription(
          data?.resp_move
            ? 'Incorrect! Opponent will respond with ' +
                data?.resp_move +
                '. Try Again.'
            : 'Incorrect Move!',
        );
      }
      handleFen(fen);
      setTimeout(() => {
        setPuzzleResult('None');
        setPuzzleHint('None');
      }, 10000);
      setResetChessboard(prev => !prev);
    }
  };

  const handleOnFailedVerification = () => setIsLoading(false);

  const {
    mutate: verifyPuzzle,
    data: verifyPuzzleResponse,
    error: verifyPuzzleError,
  } = useVerifyPuzzlesApi(
    handleOnSuccessfulVerification,
    handleOnFailedVerification,
  );

  const {
    mutate: saveAlternateMoves,
    data: saveAlternateMovesData,
    error: saveAlternateMovesError,
  } = useSaveAlternateMoves(
    () => {},
    () => {},
  );

  const {mutate: downloadGameReviewPgn, isLoading: downloadLoader} =
    useDownloadGameReviewPGN(
      () => console.log('Download successful'),
      error => console.error('Download failed', error),
    );

  const playerDetails = ({
    playerType,
    statsOnly = false,
  }: {
    playerType: 'black' | 'white';
    statsOnly?: boolean;
  }) => {
    const isWhite = playerType === 'white';
    const movesForErrors = viewgame ? viewGameMoves : streamedMoves;
    const tempErrors = calculateErrorsByColor(movesForErrors);
    const openingPrinciples = calculateOpeningPrinciplesByColor(movesForErrors);
    const principleRow = openingPrinciples[playerType];

    const amIWhite = normalizeBoardColor(playerColor) === 'white';
    const isPlayerSlot = isWhite ? amIWhite : !amIWhite;
    const fallbackName = isPlayerSlot ? player : opponent;
    const fallbackRating = isPlayerSlot ? playerRating : opponentRating;
    const displayName = isWhite
      ? players?.white || fallbackName
      : players?.black || fallbackName;
    const displayRating = isWhite
      ? players?.white_rating || fallbackRating
      : players?.black_rating || fallbackRating;

    const statRows = (
      <>
        <Spacer spacing={statsOnly ? 8 : 10} />
        <Box flexDirection="row" alignItems="center">
          <Image source={blunderIcon} style={{width: 25, height: 20}} />
          <Spacer spacing={10} horizontal />
          <CCText>{tempErrors?.[playerType]?.blunders} blunder</CCText>
        </Box>
        <Spacer spacing={10} />
        <Box flexDirection="row" alignItems="center">
          <Image source={mistakesIcon} style={{width: 25, height: 20}} />
          <Spacer spacing={10} horizontal />
          <CCText>{tempErrors?.[playerType]?.mistakes} mistakes</CCText>
        </Box>
        <Spacer spacing={10} />
        <Box flexDirection="row" alignItems="center">
          <Image source={inaccurateIcon} style={{width: 25, height: 20}} />
          <Spacer spacing={10} horizontal />
          <CCText>{tempErrors?.[playerType]?.inaccuracies} inaccuracy</CCText>
        </Box>
        {principleRow.followed > 0 ? (
          <>
            <Spacer spacing={10} />
            <Box flexDirection="row" alignItems="center">
              <OpeningPrincipleOChip tint={OPENING_PRINCIPLE_SIDEBAR_TEAL} />
              <Spacer spacing={10} horizontal />
              <CCText>
                {principleRow.followed} opening principle
                {principleRow.followed === 1 ? '' : 's'} followed
              </CCText>
            </Box>
          </>
        ) : null}
        {principleRow.violated > 0 ? (
          <>
            <Spacer spacing={10} />
            <Box flexDirection="row" alignItems="center">
              <OpeningPrincipleOChip tint={OPENING_PRINCIPLE_SIDEBAR_RED} />
              <Spacer spacing={10} horizontal />
              <CCText>
                {principleRow.violated} opening principle
                {principleRow.violated === 1 ? '' : 's'} violated
              </CCText>
            </Box>
          </>
        ) : null}
      </>
    );

    if (statsOnly) {
      return <>{statRows}</>;
    }

    return (
      <Box style={styles.eachPlayerDetails} flex={0.25}>
        <Box style={styles.eachPlayerName}>
          <Box style={isWhite ? styles.whiteBox : styles.BrownBox} />
          <CCText fontSize={FontSizes[12]} fontWeight="bold" showEllipses>
            {'  '}
            {displayName}
          </CCText>
        </Box>
        <CCText fontSize={FontSizes[11]} color={CCColors.Grey.Shade100}>
          #{displayRating}
        </CCText>
        {statRows}
      </Box>
    );
  };

  useEffect(() => {
    if (!viewgame) {
      return;
    }

    const amIWhite = normalizeBoardColor(playerColor) === 'white';
    setPlayers(prev => {
      if (prev?.white || prev?.black) {
        return prev;
      }
      return {
        white: amIWhite ? player : opponent,
        black: amIWhite ? opponent : player,
        white_rating: amIWhite ? playerRating : opponentRating,
        black_rating: amIWhite ? opponentRating : playerRating,
      };
    });
  }, [viewgame, playerColor, player, opponent, playerRating, opponentRating]);

  // In handleFen — store color alongside score
  const handleFen = useCallback(
    (
      tempFen: any,
      isPuzzle?: boolean,
      puzzleId?: string,
      lastFen: any = {},
      nextPuzzle?: () => void,
      isManualMove?: boolean,
    ) => {
      setFen2(prev => ({
        fen: tempFen?.fen,
        flag: !prev?.flag,
        lastFen: {
          ...lastFen,
          score: tempFen?.score ?? lastFen?.score,
          // ✅ FIX: store the color of the move that generated this score
          color: tempFen?.color ?? lastFen?.color,
        },
      }));
      setFen({...tempFen, lastFen});
      if (isPuzzle && puzzleId) {
        setIsPuzzleScreen({flag: true, id: puzzleId});
        setSelectedManualMove(null);
        latestBoardMoveRef.current = null;
      }
      if (isManualMove) {
        setSelectedManualMove(tempFen);
      }
    },
    [],
  );
  const resetBoardToStartingPosition = useCallback(() => {
    setCorrespondingFen(STARTING_FEN);
    setIsFenFromOutside(false);
    setSelectedManualMove(null);
    setMoveSelectedInReview('');
    setIsPuzzleScreen({flag: false, id: ''});
    setPuzzleResult('None');
    setPuzzleDescription('');
    setFen({fen: STARTING_FEN});
    setFen2(prev => ({fen: STARTING_FEN, flag: !prev?.flag, lastFen: {}}));
  }, []);

  const [chessNew] = useState(new Chess());

  const handlePieceDrop = (from, to, promotion = 'q') => {
    if (
      isPuzzleScreen?.flag &&
      isPuzzleScreen?.id &&
      tabOpen === 'POSITIONS' &&
      puzzleResult !== 'True'
    ) {
      try {
        const lastFen = fen2?.fen || fen?.fen || STARTING_FEN;
        const puzzleChess = new Chess(lastFen);
        const move = puzzleChess.move({from, to, promotion});
        if (move) {
          const pendingPuzzleMove = {
            from: move.from,
            to: move.to,
            piece: move.piece,
            san: move.san,
            newFen: puzzleChess.fen(),
            lastFen,
          };
          latestBoardMoveRef.current = pendingPuzzleMove;
          setSelectedManualMove(pendingPuzzleMove);
          setCorrespondingFen(pendingPuzzleMove.newFen);
        }
      } catch {
        latestBoardMoveRef.current = null;
      }
      setIsPuzzleScreen(prev => ({...prev, prevMove: fen}));
      setIsLoading(true);
      verifyPuzzle({puzzleId: isPuzzleScreen?.id, move: from + to});
      return false;
    } else {
      if (tabOpen?.toLowerCase() !== 'GAME REVIEW') {
        setTabOpen('GAME REVIEW');
        setPuzzleResult('None');
      }
      return false;
    }
  };

  const handleSaveAlternateMove = ({
    gameMoveId,
    alternateMoves,
  }: {
    gameMoveId: number;
    alternateMoves: any;
  }) => {
    if (tabOpen?.toUpperCase() !== 'POSITIONS') {
      saveAlternateMoves({gameMoveId, alternateMoves});
    }
  };

  const handlePositionChange = tempPosition => {
    setCorrespondingFen(tempPosition?.newFen);
    setIsFenFromOutside(true);
    latestBoardMoveRef.current = tempPosition;
    if (tabOpen?.toUpperCase() === 'POSITIONS') {
      setSelectedManualMove(tempPosition);
    }
    if (tabOpen?.toUpperCase() !== 'POSITIONS') {
      setManualMoves(prev => {
        const currentFEN = moveSelectedInReview?.fen;
        if (!currentFEN) {
          return prev;
        }
        let existingMovesForFEN = prev[currentFEN] || [];
        if (!existingMovesForFEN || existingMovesForFEN?.length === 0) {
          existingMovesForFEN = [
            {
              ...fen,
              newFen: fen2?.fen,
              lastFen: fen2?.lastFen?.fen,
              san: fen?.move,
            },
          ];
        }
        const indexOfPrev = existingMovesForFEN?.findIndex(
          element => element.newFen === tempPosition?.lastFen,
        );
        const updatedMovesForFEN = [
          ...existingMovesForFEN?.splice(0, indexOfPrev + 1),
          tempPosition,
        ];
        handleSaveAlternateMove({
          gameMoveId: moveSelectedInReview?.id,
          alternateMoves: updatedMovesForFEN?.map(i => ({
            lastFen: i?.lastFen || '',
            newFen: i?.newFen,
            san: i?.san,
          })),
        });
        return {
          ...prev,
          [currentFEN]: [...updatedMovesForFEN],
        };
      });
      setSelectedManualMove(tempPosition);
    }
  };

  useGAPageView(ScreenName.ChessAnalysis);

  const [streamedMoves, setStreamedMoves] = useState<any>([]);
  const [viewGameMoves, setViewGameMoves] = useState<any[]>([]);
  const [puzzleSummary, setPuzzleSummary] = useState({});
  const [players, setPlayers] = useState({});
  const [gameDetails, setGameDetails] = useState({});
  const [gameDetailsQuery, setGameDetailsQuery] = useState<string[]>([
    timeControl,
    timeClass,
  ]);
  const [gameReviewedByUser, setGameReviewedByUser] = useState(false);
  const [result, setResult] = useState('Loading');
  const [engineAnalyzed, setEngineAnalyzed] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisStatusText, setAnalysisStatusText] = useState('');
  const [storedAnalysisLoading, setStoredAnalysisLoading] = useState(
    !analysisMetadataChecked || effectiveAlreadyAnalyzed,
  );
  const [analysisStopped, setAnalysisStopped] = useState(false);
  const analysisSubscriptionRef = useRef<{unsubscribe: () => void} | null>(
    null,
  );

  const [resultOpacityAnimStyle, startResultOpacityAnim] = useAnimation({
    prop: 'opacity',
    start: 1,
    end: 1,
    repeat: true,
    numberOfReps: 2,
    inputRange: [0, 0.1],
    duration: 1000,
  });

  const stopGameReviewAnalysis = useCallback(() => {
    try {
      analysisSubscriptionRef.current?.unsubscribe();
      if (gameId) {
        stopBackgroundGameAnalysis(gameId, analysisMode);
      }
    } catch {
      /* best-effort stop */
    }
    analysisSubscriptionRef.current = null;
    setIsLoading(false);
    setStoredAnalysisLoading(false);
    setAnalysisStopped(true);
    setAnalysisStatusText('Game review stopped');
  }, [analysisMode, gameId]);

  const restartGameReviewAnalysis = useCallback(() => {
    setAnalysisStopped(false);
    setStoredAnalysisLoading(false);
    setShowError(false);
    setIsLoading(true);
    setAnalysisProgress(0);
    setAnalysisStatusText(
      analysisMode === 'deep'
        ? 'Starting deep analysis. This can take a few minutes...'
        : 'Starting game analysis...',
    );
    setRefresh(prev => !prev);
  }, [analysisMode]);

  useEffect(() => {
    let cancelled = false;

    setAnalysisStopped(false);
    if (!gameId || viewgame) {
      setBackendAlreadyAnalyzed(false);
      setAnalysisMetadataChecked(true);
      setStoredAnalysisLoading(false);
      return () => {
        cancelled = true;
      };
    }
    if (alreadyAnalyzed) {
      setBackendAlreadyAnalyzed(true);
      setAnalysisMetadataChecked(true);
      setStoredAnalysisLoading(true);
      return () => {
        cancelled = true;
      };
    }

    setBackendAlreadyAnalyzed(false);
    setAnalysisMetadataChecked(false);
    setStoredAnalysisLoading(true);
    setAnalysisStatusText('Loading saved game review...');

    isGameAlreadyAnalyzed(gameId)
      .then(isAnalyzed => {
        if (cancelled) {
          return;
        }
        setBackendAlreadyAnalyzed(isAnalyzed);
        setStoredAnalysisLoading(isAnalyzed);
      })
      .catch(() => {
        if (!cancelled) {
          setBackendAlreadyAnalyzed(false);
          setStoredAnalysisLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAnalysisMetadataChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [alreadyAnalyzed, gameId, viewgame]);

  useEffect(() => {
    if (analysisStopped) {
      return;
    }
    if (!analysisMetadataChecked) {
      return;
    }
    if (puzzleResult !== 'True') {
      setStreamedMoves([]);
    }
    setShowError(false);
    setStoredAnalysisLoading(effectiveAlreadyAnalyzed);
    setAnalysisStatusText('');

    if (!viewgame) {
      setIsLoading(true);
      setAnalysisStatusText(
        analysisMode === 'deep'
          ? 'Starting deep analysis. This can take a few minutes...'
          : 'Starting game analysis...',
      );
      const subscription = getBackgroundGameAnalysis(
        gameId,
        analysisMode,
        effectiveAlreadyAnalyzed,
      )
        .pipe(
          tap(data => {
            if (data?.players) {
              setPlayers(data?.players);
            }
            if (data?.game_details) {
              setGameDetails(data?.game_details);
            }
            if (data?.hasOwnProperty('game_reviewed_by_user')) {
              setGameReviewedByUser(data?.game_reviewed_by_user);
            }
            if (data?.game_result) {
              setResult(data?.game_result);
            }
            if (data?.puzzle_summary) {
              setPuzzleSummary(data?.puzzle_summary);
            }
            if (data?.game_overview) {
              setGameOverview(data?.game_overview);
            }
            if (data?.game_score_details) {
              setGameScoreDetails(data?.game_score_details);
            }
            if (typeof data?.game_engine_analyzed === 'boolean') {
              setEngineAnalyzed(data?.game_engine_analyzed);
            }
            if (data?.status === 'error') {
              setStoredAnalysisLoading(false);
              setShowError(true);
              setAnalysisStatusText('');
              return;
            }
            if (data?.status === 'loading_stored_analysis') {
              setStoredAnalysisLoading(true);
              setIsLoading(true);
              setAnalysisStatusText('Loading saved game review...');
              return;
            }
            if (data?.status === 'starting_analysis') {
              setStoredAnalysisLoading(false);
              setGameReviewedByUser(false);
              setAnalysisProgress(0);
              setAnalysisStatusText(
                data?.analysis_mode === 'deep'
                  ? 'Starting deep analysis. This can take a few minutes...'
                  : 'Starting game analysis...',
              );
              return;
            }
            if (data?.status === 'analysis_progress') {
              setStoredAnalysisLoading(false);
              setIsLoading(true);
              if (typeof data?.analysis_progress === 'number') {
                setAnalysisProgress(data.analysis_progress);
              }
              setAnalysisStatusText(
                data?.progress_message || 'Game analysis in progress...',
              );
              return;
            }
            const hasGameMoves =
              Array.isArray(data?.game_moves) && data.game_moves.length > 0;
            if (data?.status === 'Game already analyzed' || hasGameMoves) {
              setStoredAnalysisLoading(false);
              setIsLoading(false);
              setShowError(false);
              setAnalysisProgress(1);
              setAnalysisStatusText('');
              const moves = renderableGameMoves(data?.game_moves);
              if (moves.length > 0 && puzzleResult !== 'True') {
                setStreamedMoves(moves);
                const lastMove = moves[moves.length - 1];
                setFen2(prev => ({
                  ...prev,
                  fen: lastMove?.fen,
                  lastFen: {
                    score: lastMove?.score,
                    color: lastMove?.color,
                  },
                }));
              }
              return;
            }
            const moves = renderableGameMoves(data?.game_moves);
            if (moves.length > 0 && puzzleResult !== 'True') {
              setStoredAnalysisLoading(false);
              setIsLoading(false);
              setShowError(false);
              setAnalysisProgress(1);
              setAnalysisStatusText('');
              setStreamedMoves(moves);
              const lastMove = moves[moves.length - 1];
              setFen2(prev => ({
                ...prev,
                fen: lastMove?.fen,
                lastFen: {
                  score: lastMove?.score,
                  color: lastMove?.color,
                },
              }));
              return;
            }
            if (data?.analysis?.fen && puzzleResult !== 'True') {
              setStoredAnalysisLoading(false);
              setIsLoading(false);
              setAnalysisStatusText('');
              const nextMove = {...data, ...data?.analysis};
              if (!isRenderableGameMove(nextMove)) {
                return;
              }
              if (data?.total_moves) {
                setAnalysisProgress((data?.move_index * 2) / data?.total_moves);
              }
              setStreamedMoves(prevMoves => {
                if (prevMoves?.find(m => m?.fen === nextMove?.fen)) {
                  return prevMoves;
                }
                return [...(prevMoves ?? []), nextMove];
              });
              setFen2(prev => ({
                ...prev,
                fen: nextMove?.fen,
                lastFen: {
                  score: nextMove?.score,
                  color: nextMove?.color,
                },
              }));
              return;
            }
            if (data?.user_points) {
              useLoggedInUserStore.setState({
                gems: data?.user_points?.points?.points,
                pointsToNextLevel:
                  data?.user_points?.points?.points_to_next_level,
                level: data?.user_points?.points?.level,
                netScore: data?.user_points?.net_score,
              });
            } else if (data?.is_permitted === false) {
              setSubscriptionModalOpen(true);
            }
          }),
        )
        .subscribe({
          error: () => {
            analysisSubscriptionRef.current = null;
            setStoredAnalysisLoading(false);
            setIsLoading(false);
            setShowError(true);
            setAnalysisProgress(1);
            setAnalysisStatusText('');
          },
          complete: () => {
            analysisSubscriptionRef.current = null;
            setStoredAnalysisLoading(false);
          },
        });
      analysisSubscriptionRef.current = subscription;

      return () => {
        subscription.unsubscribe();
        if (analysisSubscriptionRef.current === subscription) {
          analysisSubscriptionRef.current = null;
        }
      };
    }
  }, [
    analysisMetadataChecked,
    analysisMode,
    analysisStopped,
    effectiveAlreadyAnalyzed,
    gameId,
    refresh,
    setSubscriptionModalOpen,
  ]);

  const handleNextPuzzle = () => {
    setPuzzleResult('None');
    setPuzzleDescription('');
    setOpenSuccessModal({flag: false, correctSeq: []});
  };

  const handleAnalysisModeChange = (mode: BrowserAnalysisMode) => {
    if (mode === analysisMode) {
      return;
    }
    if (gameId) {
      stopBackgroundGameAnalysis(gameId, analysisMode);
    }
    resetBoardToStartingPosition();
    setStreamedMoves([]);
    setAnalysisProgress(0);
    setAnalysisStatusText(
      mode === 'deep'
        ? 'Starting deep analysis. This can take a few minutes...'
        : 'Starting game analysis...',
    );
    setIsLoading(true);
    setAnalysisMode(mode);
  };

  const activeMoves = viewgame ? viewGameMoves : streamedMoves;

  const reviewBoardSize = useMemo(() => {
    const viewportWidthBound =
      viewportWidth -
      REVIEW_LEFT_PANEL_WIDTH -
      REVIEW_RIGHT_PANEL_WIDTH -
      REVIEW_CONTAINER_HORIZONTAL_SPACE -
      REVIEW_BOARD_SIDE_GAP;
    const columnWidthBound =
      boardColumnWidth > 0
        ? boardColumnWidth - REVIEW_BOARD_SIDE_GAP
        : viewportWidthBound;
    const widthBound = Math.max(0, columnWidthBound);
    const heightBound = viewportHeight - REVIEW_BOARD_VERTICAL_RESERVE;
    return Math.max(
      REVIEW_MIN_DESKTOP_BOARD_SIZE,
      Math.floor(Math.min(widthBound, heightBound)),
    );
  }, [boardColumnWidth, viewportHeight, viewportWidth]);

  const MOBILE_BOARD_HORIZONTAL_PADDING = 24;
  const mobileBoardSize = useMemo(() => {
    const widthBound = viewportWidth - MOBILE_BOARD_HORIZONTAL_PADDING;
    const heightBound =
      isTablet || isCompactDesktopLayout
        ? viewportHeight * 0.58
        : Math.max(240, viewportHeight * 0.6 - 132);
    return Math.max(
      isTablet || isCompactDesktopLayout ? 320 : 210,
      Math.floor(Math.min(widthBound, heightBound)),
    );
  }, [isCompactDesktopLayout, isTablet, viewportWidth, viewportHeight]);

  const mobileSheetMinHeight = useMemo(
    () => Math.max(180, Math.floor(viewportHeight * 0.3)),
    [viewportHeight],
  );

  const reviewBoardWrapWidth = reviewBoardSize + REVIEW_BOARD_SIDE_GAP;

  const moveIndexInReview = useMemo(() => {
    if (!moveSelectedInReview || !Array.isArray(activeMoves)) {
      return -1;
    }
    return activeMoves.findIndex(move =>
      moveSelectedInReview?.id && move?.id
        ? move.id === moveSelectedInReview.id
        : move?.fen === moveSelectedInReview?.fen,
    );
  }, [activeMoves, moveSelectedInReview]);

  const selectedMoveLabel = useMemo(() => {
    if (moveIndexInReview < 0) {
      return 'Starting position';
    }
    const move = activeMoves?.[moveIndexInReview];
    const prefix =
      move?.move_index || Math.floor(Math.max(0, moveIndexInReview) / 2) + 1;
    return `${prefix}${move?.color === 'black' ? '...' : '.'} ${
      move?.move || ''
    }`;
  }, [activeMoves, moveIndexInReview]);

  // Return -1 sentinel when no move is selected so the indicator line
  // is hidden rather than snapping to 0%.
  const timelineCurrentPct = useMemo(() => {
    const total = Array.isArray(activeMoves) ? activeMoves.length : 0;
    if (total <= 1 || moveIndexInReview < 0) {
      return -1;
    }
    const clampedIndex = Math.min(moveIndexInReview, total - 1);
    return Math.max(0, Math.min(100, (clampedIndex / (total - 1)) * 100));
  }, [activeMoves, moveIndexInReview]);

  // ─── Evaluation graph ──────────────────────────────────────────────────────
  const timelineGraph = useMemo(() => {
    const moves = Array.isArray(activeMoves)
      ? activeMoves.filter(move => move && (move.move || move.fen))
      : [];

    const width = 660;
    const height = 132;
    const centerY = height / 2;

    // Use total game moves if known, fallback to analyzed moves count
    // This ensures the timeline x-axis represents the FULL game, not just analyzed portion
    const totalGameMoves =
      Number(gameDetails?.total_number_of_moves) ||
      Number(gameOverviewDetails?.total_number_of_moves) ||
      moves.length;

    const clampCp = (value: number): number => {
      if (value >= MATE_THRESHOLD) {
        return 900;
      }
      if (value <= -MATE_THRESHOLD) {
        return -900;
      }
      return Math.max(-900, Math.min(900, value));
    };

    const scoreToY = (score: number): number => {
      const whiteNormalized = clampCp(score) / 900;
      // Lichess convention: white winning → UP, black winning → DOWN (always)
      return Math.max(4, Math.min(height - 4, centerY - whiteNormalized * 58));
    };

    if (moves.length === 0) {
      return {
        width,
        height,
        linePoints: `0,${centerY} ${width},${centerY}`,
        bottomAdvantageFillPoints: `0,${height} 0,${centerY} ${width},${centerY} ${width},${height}`,
        dots: [],
        phaseXs: [],
      };
    }

    let lastScore: number | null = null;

    // ✅ FIX: x-axis spans the FULL expected game length, not just analyzed moves
    // This way, partial analysis renders progressively from left, with a smooth
    // extension to the right edge (showing flat extrapolation of last known score).
    const totalSlots = Math.max(moves.length, totalGameMoves);

    const analyzedPoints = moves.map((move, index) => {
      const whiteScore = scoreFromUserToWhitePerspective(
        move?.score,
        move?.color,
      );
      if (whiteScore !== null) {
        lastScore = whiteScore;
      }
      const scoreToUse = lastScore ?? 0;
      // x scales over total slots, not just analyzed count
      const x = totalSlots <= 1 ? 0 : (index / (totalSlots - 1)) * width;
      const y = scoreToY(scoreToUse);
      return {x, y, move};
    });

    // ✅ FIX: Always extend the line to width (right edge) so the polygon
    // covers the full graph. The extension is flat at the last known score —
    // visually communicating "this is the eval so far; rest not analyzed yet".
    const lastAnalyzed = analyzedPoints[analyzedPoints.length - 1];
    const points =
      lastAnalyzed.x < width
        ? [...analyzedPoints, {x: width, y: lastAnalyzed.y, move: null}]
        : analyzedPoints;

    const linePoints = points
      .map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(' ');

    // ✅ FIX: Polygon now properly closes across full width
    const firstY = points[0].y.toFixed(1);
    const bottomAdvantageFillPoints = [
      `0,${height}`, // bottom-left corner
      `0,${firstY}`, // up to first point's y
      linePoints, // the line across the full width
      `${width},${height}`, // bottom-right corner (always at width now)
    ].join(' ');

    // Dots only on actually-analyzed moves, not the synthetic extension
    const dots = analyzedPoints
      .map((point, index) => {
        const type =
          point.move?.score != null ? evaluateMove(point.move) : null;
        const color =
          type === 'Blunder'
            ? '#E17846'
            : type === 'Mistake'
            ? '#FACF47'
            : type === 'Inaccuracy'
            ? '#67B3E1'
            : type === 'PrincipleViolation'
            ? '#E17846'
            : '';
        return color ? {...point, color, index} : null;
      })
      .filter(Boolean);

    const phaseXs = [0.33, 0.66].map(ratio => width * ratio);

    return {
      width,
      height,
      linePoints,
      bottomAdvantageFillPoints,
      dots,
      phaseXs,
    };
  }, [
    activeMoves,
    gameDetails?.total_number_of_moves,
    gameOverviewDetails?.total_number_of_moves,
  ]);

  // ─── Display score for the eval pill ──────────────────────────────────────
  // scoreFromUserToWhitePerspective now always returns the
  // white-perspective score; we orient it here for display only.
  const timelineDisplayScore = useMemo(() => {
    const whiteScore = scoreFromUserToWhitePerspective(
      fen2?.lastFen?.score,
      fen2?.lastFen?.color,
    );
    if (whiteScore === null) {
      return 'Even';
    }

    if (Math.abs(whiteScore) >= MATE_THRESHOLD) {
      return whiteScore > 0 ? '+M' : '-M';
    }
    if (whiteScore === 0) {
      return 'Even';
    }
    return `${whiteScore > 0 ? '+' : ''}${(whiteScore / 100).toFixed(2)}`;
  }, [fen2?.lastFen?.score, fen2?.lastFen?.color]);

  // added handleFen to dependency array
  const goToMoveIndex = useCallback(
    (targetIndex: number) => {
      const moves = Array.isArray(activeMoves) ? activeMoves : [];
      if (moves.length === 0) {
        resetBoardToStartingPosition();
        return;
      }
      if (targetIndex < 0) {
        resetBoardToStartingPosition();
        return;
      }
      const boundedIndex = Math.max(0, Math.min(moves.length - 1, targetIndex));
      const targetMove = moves[boundedIndex];
      if (!targetMove) {
        return;
      }
      const previousMove = boundedIndex > 0 ? moves[boundedIndex - 1] : {};
      handleFen(targetMove, false, '', previousMove, () => {}, false);
      setIsFenFromOutside(false);
      handleSelectMoveInReview(targetMove);
    },
    [
      activeMoves,
      handleFen,
      handleSelectMoveInReview,
      resetBoardToStartingPosition,
    ],
  );

  const handleTimelinePress = useCallback(
    event => {
      const moves = Array.isArray(activeMoves)
        ? activeMoves.filter(move => move && (move.move || move.fen))
        : [];
      if (!moves.length || !timelineGraphWidth) {
        return;
      }
      const locationX =
        event?.nativeEvent?.locationX ??
        event?.nativeEvent?.offsetX ??
        event?.nativeEvent?.pageX;
      const x = Math.max(
        0,
        Math.min(Number(locationX) || 0, timelineGraphWidth),
      );
      const targetIndex =
        moves.length <= 1
          ? 0
          : Math.round((x / timelineGraphWidth) * (moves.length - 1));
      goToMoveIndex(targetIndex);
    },
    [activeMoves, goToMoveIndex, timelineGraphWidth],
  );

  const formatResultScore = () => {
    const normalized = String(result || gameOverviewDetails?.result || '')
      .trim()
      .toLowerCase();
    if (normalized.includes('1-0') || normalized === 'white') {
      return '1-0';
    }
    if (normalized.includes('0-1') || normalized === 'black') {
      return '0-1';
    }
    if (normalized.includes('draw') || normalized.includes('1/2')) {
      return '1/2';
    }
    const playerIsWhite = normalizeBoardColor(playerColor) === 'white';
    if (normalized.includes('won') || normalized.includes('win')) {
      return playerIsWhite ? '1-0' : '0-1';
    }
    if (normalized.includes('lost') || normalized.includes('loss')) {
      return playerIsWhite ? '0-1' : '1-0';
    }
    return '1-0';
  };

  const resultTitle = () => {
    const normalized = String(result || '').toLowerCase();
    if (normalized.includes('lost') || normalized.includes('loss')) {
      return 'You lost';
    }
    if (normalized.includes('draw')) {
      return 'Draw';
    }
    if (normalized && normalized !== 'loading') {
      return normalized.includes('won') || normalized.includes('win')
        ? 'You won'
        : capitalizeFirstLetter(normalized);
    }
    return 'Game reviewed';
  };

  const formatGameDate = () => {
    const raw = gameDetails?.end_time || gameOverviewDetails?.end_time;
    if (!raw) {
      return '';
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDisplayPlayer = (playerType: 'black' | 'white') => {
    const isWhite = playerType === 'white';
    const amIWhite = normalizeBoardColor(playerColor) === 'white';
    const isPlayerSlot = isWhite ? amIWhite : !amIWhite;
    return {
      name: isWhite
        ? players?.white || (isPlayerSlot ? player : opponent)
        : players?.black || (isPlayerSlot ? player : opponent),
      rating: isWhite
        ? players?.white_rating ||
          (isPlayerSlot ? playerRating : opponentRating)
        : players?.black_rating ||
          (isPlayerSlot ? playerRating : opponentRating),
      isPlayerSlot,
    };
  };

  const getMoveDerivedAccuracyForColor = (playerType: 'black' | 'white') => {
    const moves = Array.isArray(activeMoves) ? activeMoves : [];
    if (!moves.length) {
      return null;
    }
    const hasColorMoves = moves.some(
      move => normalizeBoardColor(move?.color) === playerType,
    );
    if (!hasColorMoves) {
      return null;
    }
    const colorMoves = moves.filter(
      move => normalizeBoardColor(move?.color) === playerType,
    );
    const {inaccuracies, mistakes, blunders} =
      calculateErrorsByColor(moves)[playerType];
    return accuracy(inaccuracies, mistakes, blunders, colorMoves.length);
  };

  const getAccuracyForColor = (playerType: 'black' | 'white') => {
    const amIWhite = normalizeBoardColor(playerColor) === 'white';
    const isPlayerSlot = playerType === 'white' ? amIWhite : !amIWhite;
    const summaryAccuracy = isPlayerSlot
      ? firstFiniteNumber(
          gameScoreDetails?.average_accuracy,
          gameScoreDetails?.averageAccuracy,
          gameScoreDetails?.accuracy,
          gameOverviewDetails?.accuracy,
          gameDetails?.accuracy,
        )
      : firstFiniteNumber(
          gameScoreDetails?.opponent_average_accuracy,
          gameScoreDetails?.opponentAverageAccuracy,
          gameScoreDetails?.opponent_accuracy,
          gameOverviewDetails?.opponent_accuracy,
          gameDetails?.opponent_accuracy,
        );
    const numeric =
      summaryAccuracy && summaryAccuracy > 0
        ? summaryAccuracy
        : getMoveDerivedAccuracyForColor(playerType);
    return numeric !== null && Number.isFinite(numeric)
      ? numeric.toFixed(1)
      : '-';
  };

  const renderStatPill = (
    value: number,
    label: string,
    variant: 'blunder' | 'mistake' | 'inaccuracy',
    dark = false,
  ) => {
    const colors = {
      blunder: {
        bg: dark ? 'rgba(255,255,255,0.08)' : '#FFF6F2',
        value: dark ? '#FF9B7A' : '#CD2C39',
        label: dark ? 'rgba(255,255,255,0.75)' : '#B86A52',
      },
      mistake: {
        bg: dark ? 'rgba(255,255,255,0.08)' : '#FCF3DC',
        value: dark ? '#FACF47' : '#9A7B1E',
        label: dark ? 'rgba(255,255,255,0.75)' : '#9A7B1E',
      },
      inaccuracy: {
        bg: dark ? 'rgba(255,255,255,0.08)' : '#EFF6FE',
        value: dark ? '#8FD0FF' : '#2A7BB8',
        label: dark ? 'rgba(255,255,255,0.75)' : '#2A7BB8',
      },
    }[variant];
    return (
      <View style={[styles.statPill, {backgroundColor: colors.bg}]}>
        <CCText style={[styles.statNumber, {color: colors.value}]}>
          {value || 0}
        </CCText>
        <CCText style={[styles.statLabel, {color: colors.label}]}>
          {label}
        </CCText>
      </View>
    );
  };

  const renderPrinciplePill = (
    value: number,
    label: string,
    color: string,
    backgroundColor: string,
    dark = false,
  ) => (
    <View style={[styles.principlePill, {backgroundColor}]}>
      <View style={[styles.principleIcon, {backgroundColor: color}]}>
        <Ionicons
          name={label === 'followed' ? 'checkmark' : 'close'}
          size={12}
          color="#FFFFFF"
        />
      </View>
      <CCText
        style={[
          styles.principlePillText,
          {color: dark ? 'rgba(255,255,255,0.86)' : color},
        ]}>
        {value || 0} {label}
      </CCText>
    </View>
  );

  const renderReviewPlayerCard = (playerType: 'black' | 'white') => {
    const display = getDisplayPlayer(playerType);
    const dark = playerType === 'black';
    const errors = calculateErrorsByColor(activeMoves)?.[playerType] || {};
    const principles =
      calculateOpeningPrinciplesByColor(activeMoves)?.[playerType] || {};
    const hasOpeningPrinciples =
      Number(principles.followed || 0) > 0 ||
      Number(principles.violated || 0) > 0;
    const initial = String(display.name || playerType[0])
      .charAt(0)
      .toUpperCase();

    return (
      <View
        style={[styles.reviewPlayerCard, dark && styles.reviewPlayerCardDark]}>
        <View style={styles.playerHeader}>
          <View style={[styles.playerAvatar, dark && styles.playerAvatarDark]}>
            <CCText style={styles.playerAvatarText}>{initial}</CCText>
          </View>
          <View style={styles.playerInfo}>
            <View style={{flexDirection: 'row', alignItems: 'center'}}>
              <CCText
                showEllipses
                style={[styles.playerName, dark && styles.playerNameDark]}>
                {display.name || (playerType === 'white' ? 'White' : 'Black')}
              </CCText>
              {display.isPlayerSlot && (
                <View style={styles.youBadge}>
                  <CCText style={styles.youBadgeText}>YOU</CCText>
                </View>
              )}
            </View>
            <CCText
              style={[styles.playerRating, dark && styles.playerRatingDark]}>
              {capitalizeFirstLetter(gameDetails?.type || timeClass || 'Game')}{' '}
              {display.rating || '-'}
            </CCText>
          </View>
          <View>
            <CCText
              style={[styles.accuracyValue, dark && styles.accuracyValueDark]}>
              {getAccuracyForColor(playerType)}
            </CCText>
            <CCText style={styles.accuracyLabel}>Accuracy</CCText>
          </View>
        </View>
        <View style={styles.statPillRow}>
          {renderStatPill(errors.blunders, '?? blunder', 'blunder', dark)}
          {renderStatPill(errors.mistakes, '? mistake', 'mistake', dark)}
          {renderStatPill(errors.inaccuracies, '?! inacc.', 'inaccuracy', dark)}
        </View>
        {hasOpeningPrinciples ? (
          <View
            style={[
              styles.principleSection,
              dark && styles.principleSectionDark,
            ]}>
            <CCText
              style={[
                styles.principleTitle,
                dark && styles.principleTitleDark,
              ]}>
              Opening principles
            </CCText>
            <View style={styles.statPillRow}>
              {renderPrinciplePill(
                principles.followed,
                'followed',
                '#3DAB9E',
                dark ? 'rgba(61,171,158,0.18)' : '#DFF5EE',
                dark,
              )}
              {renderPrinciplePill(
                principles.violated,
                'violated',
                '#E17846',
                dark ? 'rgba(225,120,70,0.18)' : '#FFF1EE',
                dark,
              )}
            </View>
          </View>
        ) : null}
      </View>
    );
  };

  // ─── Evaluation timeline JSX ───────────────────────────────────────────────
  const evaluationTimelineComponent = (
    <View style={styles.timelineCard}>
      <View style={styles.timelineHeader}>
        <CCText style={styles.timelineTitle}>Evaluation timeline</CCText>
        <CCText style={styles.timelineEvalPill}>{timelineDisplayScore}</CCText>
      </View>
      <Pressable
        style={[
          styles.timelineGraph,
          {
            backgroundColor: topPlayerType === 'white' ? '#E7DCC8' : '#4D3F37',
          },
        ]}
        onLayout={event =>
          setTimelineGraphWidth(event.nativeEvent.layout.width)
        }
        onPress={handleTimelinePress}>
        <Svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${timelineGraph.width} ${timelineGraph.height}`}
          preserveAspectRatio="none"
          pointerEvents="none">
          <Polygon
            points={timelineGraph.bottomAdvantageFillPoints}
            fill={bottomPlayerType === 'white' ? '#E7DCC8' : '#4D3F37'}
          />
          <Line
            x1="0"
            y1={timelineGraph.height / 2}
            x2={timelineGraph.width}
            y2={timelineGraph.height / 2}
            stroke="rgba(255,255,255,0.32)"
            strokeWidth="1"
          />
          {timelineGraph.phaseXs.map((x, idx) => (
            <Line
              key={`timeline-phase-${idx}`}
              x1={x}
              y1="0"
              x2={x}
              y2={timelineGraph.height}
              stroke="rgba(255,255,255,0.16)"
              strokeWidth="1"
            />
          ))}
          <Polyline
            points={timelineGraph.linePoints}
            fill="none"
            stroke="#E8732F"
            strokeWidth="2.8"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {timelineGraph.dots.map(dot => (
            <Circle
              key={`timeline-dot-${dot.index}`}
              cx={dot.x}
              cy={dot.y}
              r="5"
              fill={dot.color}
              stroke="#FFFFFF"
              strokeWidth="2"
            />
          ))}
        </Svg>
        {/* only render the current-move indicator when a move is selected */}
        {timelineCurrentPct >= 0 && (
          <View
            style={[
              styles.timelineCurrentLine,
              {left: `${timelineCurrentPct}%`},
            ]}>
            <View style={styles.timelineCurrentKnob} />
          </View>
        )}
      </Pressable>
      <View style={styles.timelineLegend}>
        {[
          ['#E17846', 'Blunder'],
          ['#FACF47', 'Mistake'],
          ['#67B3E1', 'Inaccuracy'],
        ].map(([color, label]) => (
          <View key={label} style={styles.legendItem}>
            <View style={[styles.legendDot, {backgroundColor: color}]} />
            <CCText style={styles.legendText}>{label}</CCText>
          </View>
        ))}
        <View style={[styles.legendItem, styles.currentLegendItem]}>
          <View style={styles.currentLegendLine} />
          <CCText style={styles.legendText}>Current move</CCText>
        </View>
      </View>
    </View>
  );

  const renderLeftGameDetailsContent = (showBackButton = true) => (
    <>
      {showBackButton ? (
        <Pressable
          onPress={() => navigation.navigate(ScreenName?.Games)}
          style={styles.reviewBackButton}>
          <Ionicons name="arrow-back" size={17} color="#73695F" />
          <CCText style={styles.reviewBackText}>All games</CCText>
        </Pressable>
      ) : null}
      <View style={styles.resultCard}>
        <View style={styles.resultTopRow}>
          <View
            style={
              resultTitle().includes('lost')
                ? styles.resultScorePillLost
                : styles.resultScorePill
            }>
            <CCText
              style={
                resultTitle().includes('lost')
                  ? styles.resultTitleLost
                  : styles.resultScoreText
              }>
              {formatResultScore()}
            </CCText>
          </View>
          <View style={styles.resultTitleGroup}>
            <CCText
              style={
                resultTitle().includes('lost')
                  ? styles.resultTitleLost
                  : styles.resultTitle
              }>
              {resultTitle()}
            </CCText>
          </View>
          <CCText showEllipses style={styles.openingName}>
            {gameDetails?.opening || gameOverviewDetails?.opening || 'Opening'}
          </CCText>
        </View>
        <View style={styles.metaPillRow}>
          <View style={styles.metaPill}>
            <CCText style={styles.metaPillText}>
              {convertTimeControlToMins(gameDetails?.time_control) ||
                timeControl}{' '}
              min - {capitalizeFirstLetter(gameDetails?.type || timeClass)}
            </CCText>
          </View>
          <View style={styles.metaPill}>
            <CCText style={styles.metaPillText}>
              {activeMoves?.length || gameDetails?.total_number_of_moves || 0}{' '}
              moves
            </CCText>
          </View>
          {formatGameDate() ? (
            <View style={styles.metaPill}>
              <CCText style={styles.metaPillText}>{formatGameDate()}</CCText>
            </View>
          ) : null}
        </View>
      </View>
      {evaluationTimelineComponent}
      {renderReviewPlayerCard(topPlayerType)}
      {renderReviewPlayerCard(bottomPlayerType)}
      {!isModelGameView ? (
        <View style={styles.insightsCard}>
          <CCText style={styles.insightsTitle}>Your insights</CCText>
          <View style={styles.insightGrid}>
            {[
              {
                label: 'Accuracy',
                value:
                  gameScoreDetails?.average_accuracy?.toFixed?.(1) ||
                  getAccuracyForColor(bottomPlayerType),
              },
              {
                label: 'Blunders < 10s',
                value:
                  gameScoreDetails?.count?.blunder_in_less_than_ten_secs || 0,
              },
              {
                label: 'Remaining blunders',
                value:
                  (gameScoreDetails?.no_of_blunders || 0) -
                    (gameScoreDetails?.count?.blunder_in_less_than_ten_secs ||
                      0) || 0,
              },
              {
                label: 'First-try wins',
                value: gameScoreDetails?.count?.first_attempt_puzzles || 0,
              },
            ].map(item => (
              <View key={item.label} style={styles.insightTile}>
                <CCText style={styles.insightValue}>{item.value}</CCText>
                <CCText style={styles.insightLabel}>{item.label}</CCText>
              </View>
            ))}
          </View>
        </View>
      ) : null}
      {!isModelGameView && (
        <CCButton
          onPress={() => downloadGameReviewPgn({gameUuid: gameId})}
          style={styles.downloadButton}>
          <Ionicons name="download-outline" size={17} />
          <CCText style={styles.downloadButtonText}>PGN</CCText>
        </CCButton>
      )}
    </>
  );

  const leftGameDetailsComponent = (
    <ScrollView
      style={[
        styles.Leftcontainer,
        isMobileOrTab && styles.LeftcontainerMobile,
      ]}
      showsVerticalScrollIndicator={false}>
      {renderLeftGameDetailsContent()}
    </ScrollView>
  );

  const boardNavigationComponent = (mobile = false) => (
    <View style={[styles.boardNav, mobile && styles.mobileBoardNav]}>
      <Pressable
        onPress={() => goToMoveIndex(-1)}
        style={[styles.boardNavButton, mobile && styles.mobileBoardNavButton]}>
        <Ionicons name="play-back" size={18} color="#4D3F37" />
      </Pressable>
      <Pressable
        onPress={() => goToMoveIndex(moveIndexInReview - 1)}
        style={[styles.boardNavButton, mobile && styles.mobileBoardNavButton]}>
        <Ionicons name="chevron-back" size={18} color="#4D3F37" />
      </Pressable>
      <CCText
        showEllipses
        style={[styles.boardMoveLabel, mobile && styles.mobileBoardMoveLabel]}>
        {selectedMoveLabel}
      </CCText>
      <Pressable
        onPress={() => goToMoveIndex(moveIndexInReview + 1)}
        style={[styles.boardNavButton, mobile && styles.mobileBoardNavButton]}>
        <Ionicons name="chevron-forward" size={18} color="#4D3F37" />
      </Pressable>
      <Pressable
        onPress={() => goToMoveIndex((activeMoves?.length || 1) - 1)}
        style={[styles.boardNavButton, mobile && styles.mobileBoardNavButton]}>
        <Ionicons name="play-forward" size={18} color="#4D3F37" />
      </Pressable>
      <Pressable
        onPress={() => {
          setHasManualBoardOrientation(true);
          setBoardOrientation(prev => (prev === 'white' ? 'black' : 'white'));
        }}
        accessibilityRole="button"
        accessibilityLabel="Flip board"
        style={[styles.boardNavButton, mobile && styles.mobileBoardNavButton]}>
        <Ionicons name="swap-vertical" size={18} color="#4D3F37" />
      </Pressable>
    </View>
  );

  const shouldShowPuzzleFeedback =
    tabOpen?.toUpperCase() === 'POSITIONS' && Boolean(puzzleDescription);

  const puzzleFeedbackComponent = shouldShowPuzzleFeedback ? (
    <Animated.View
      style={[
        styles.puzzleFeedback,
        resultOpacityAnimStyle,
        {
          backgroundColor:
            puzzleResult === 'True' || puzzleDescription === 'Correct Answer!'
              ? CCColors.Green
              : CCColors.Red,
        },
      ]}>
      <Box flexDirection="row" alignItems="center" flex={1}>
        {puzzleDescription.length > 20 && (
          <Image
            resizeMethod="resize"
            source={require('@/Assets/Images/Svgs/hint.svg')}
            alt="hint"
            style={{width: 24, height: 24, marginLeft: 10}}
          />
        )}
        <CCText
          textAlign={puzzleDescription.length > 20 ? 'justify' : 'center'}
          style={[
            styles.puzzleFeedbackText,
            {
              padding: isMobileOrTab ? 5 : 10,
              fontSize: isMobileOrTab ? 12 : 15,
              color: CCColors.White,
            },
          ]}>
          {puzzleDescription.length > 70
            ? 'Incorrect Move!'
            : puzzleDescription === 'None'
            ? 'Next move to play'
            : puzzleDescription}
        </CCText>
      </Box>
    </Animated.View>
  ) : null;

  const chessboardComponentJSX = (
    <View
      style={styles.boardColumn}
      onLayout={event =>
        setBoardColumnWidth(event.nativeEvent.layout.width || 0)
      }>
      <View style={styles.boardColumnContent}>
        <View style={[styles.boardWithEval, {width: reviewBoardWrapWidth}]}>
          <View style={[styles.centerWinningBar, {height: reviewBoardSize}]}>
            <WinningBar
              whiteWinRatio={
                calculateWinningChances(fen2?.lastFen?.score)?.white / 100
              }
              blackWinRatio={
                calculateWinningChances(fen2?.lastFen?.score)?.black / 100
              }
              reverse={boardOrientation !== 'white'}
            />
          </View>
          <View
            style={[
              styles.boardSurface,
              {width: reviewBoardSize, height: reviewBoardSize},
            ]}>
            <ChessboardWithArrow
              setEngineInfo={setEngineInfo}
              resetChessboard={resetChessboard}
              position={fen2?.fen}
              onPieceDropExtension={handlePieceDrop}
              arePremovesAllowed={false}
              lastMoveFen={fen2?.lastFen?.fen}
              handleNewFen={handlePositionChange}
              boardOrientation={boardOrientation}
              colorTheme={chessboardColorTheme}
            />
          </View>
        </View>
        <View style={[styles.puzzleFeedbackSlot, {width: reviewBoardSize}]}>
          {puzzleFeedbackComponent}
        </View>
        {boardNavigationComponent(false)}
      </View>
    </View>
  );

  const analysisRightPanelComponent = (
    <AnalysisRightComponent
      infocolor={infocolor}
      attemptId={attemptId}
      isStaff={isStaff}
      puzzleId={Number(isPuzzleScreen.id)}
      fullHint2={fullHint2}
      fullHint={fullHint}
      isFenFromOutside={isFenFromOutside}
      setIsFenFromOutside={setIsFenFromOutside}
      fen2={correspondingFen}
      setPuzzleDescription={setPuzzleDescription}
      handleFen={handleFen}
      gameMoves={streamedMoves}
      puzzleSummary={puzzleSummary}
      setPuzzleSummary={setPuzzleSummary}
      players={players}
      handleNextPuzzlReset={handleNextPuzzle}
      gameReviewedByUser={gameReviewedByUser}
      result={result}
      refreshPuzzles={refresh}
      analysisInProgress={
        !effectiveAlreadyAnalyzed &&
        !storedAnalysisLoading &&
        !analysisStopped &&
        analysisProgress < 1
      }
      moveListLoading={
        storedAnalysisLoading || (!analysisStopped && analysisProgress < 1)
      }
      analysisStopped={analysisStopped}
      onStopAnalysis={stopGameReviewAnalysis}
      onRestartAnalysis={restartGameReviewAnalysis}
      gameOverviewDetails={gameOverviewDetails}
      handleSelectedTabChecks={handleSelectedTabChecks}
      manualMoves={manualMoves}
      currentPlayingFen={fen2?.fen}
      selectedManualMove={selectedManualMove}
      handleSelectMoveInReview={handleSelectMoveInReview}
      handleSaveAlternateMove={handleSaveAlternateMove}
      tabOpen={tabOpen}
      viewgame={viewgame}
      gameid={gameId}
      SetPlayerColor={setPlayerColor}
      setViewGameMoves={setViewGameMoves}
      setPlayers={setPlayers}
      setGameDetailsQuery={setGameDetailsQuery}
      gameScoreDetails={gameScoreDetails}
      playerAccuracy={getAccuracyForColor(bottomPlayerType)}
      analysisProgress={analysisProgress}
      analysisStatusText={analysisStatusText}
      analysisMode={analysisMode}
      onAnalysisModeChange={handleAnalysisModeChange}
      canChangeAnalysisMode={!isModelGameView && !viewgame}
      selectedMoveInReview={moveSelectedInReview}
    />
  );

  return (
    <SafeAreaWrapper>
      <SlideModal
        type="center"
        modalHeaderTitle={
          <CCText fontSize={FontSizes[14]}>Patience is a Chess Virtue!</CCText>
        }
        visible={false}>
        <Box margin={24}>
          <CCText fontSize={FontSizes[14]} style={{margin: 12}}>
            ♟️ Oops! Already analyzing one of your games. Let's not double-rook
            it. Back in 2! 🕒
          </CCText>
          <Box flexDirection="row" justifyContent="flex-end">
            <CCButton
              onPress={() => {
                navigation.navigate(ScreenName?.Games);
              }}>
              Ok
            </CCButton>
          </Box>
        </Box>
      </SlideModal>
      <Loader loading={isLoading && !analysisStatusText} />
      <View style={isMobileOrTab ? styles.mobileContainer : styles.container}>
        {isMobileOrTab ? (
          <ScrollView
            style={styles.mobilePageScroll}
            contentContainerStyle={styles.mobilePageContent}
            showsVerticalScrollIndicator={false}>
            <View style={styles.mobileHeader}>
              <View style={styles.mobileHeaderLeft}>
                <Pressable
                  onPress={() => navigation.navigate(ScreenName?.Games)}
                  style={styles.mobileBackButton}>
                  <Ionicons name="arrow-back" size={18} color="#73695F" />
                  <CCText style={styles.reviewBackText}>All games</CCText>
                </Pressable>
              </View>
            </View>
            <View style={styles.mobileBoardWrap}>
              <View style={styles.mobileBoardRow}>
                <View
                  style={{
                    width: mobileBoardSize,
                    height: mobileBoardSize,
                  }}>
                  <ChessboardComponent
                    setEngineInfo={setEngineInfo}
                    resetChessboard={resetChessboard}
                    position={fen2?.fen}
                    onPieceDropExtension={handlePieceDrop}
                    lastMoveFen={fen2?.lastFen?.fen}
                    handleNewFen={handlePositionChange}
                    boardOrientation={boardOrientation}
                    colorTheme={chessboardColorTheme}
                  />
                </View>
              </View>
              <View
                style={[
                  styles.puzzleFeedbackSlot,
                  {width: mobileBoardSize, marginTop: 8},
                ]}>
                {puzzleFeedbackComponent}
              </View>
              {boardNavigationComponent(true)}
            </View>
            <View style={styles.mobileDetailsSection}>
              {renderLeftGameDetailsContent(false)}
            </View>
          </ScrollView>
        ) : (
          <>
            {leftGameDetailsComponent}
            {chessboardComponentJSX}
          </>
        )}
        <ConditionalRender
          condition={isMobileOrTab}
          childrenA={
            <BottomSheet inline minHeight={mobileSheetMinHeight}>
              {analysisRightPanelComponent}
            </BottomSheet>
          }
          childrenB={
            <Box
              onLayout={onRightColumnLayout}
              style={[
                styles.movesList,
                isMobileOrTab && styles.movesListMobile,
              ]}>
              {analysisRightPanelComponent}
            </Box>
          }
        />
        <SuccessModal
          fen={fen2?.fen}
          boardOrientation={boardOrientation}
          autoPlayMoves={openSuccessModal?.correctSeq}
          open={openSuccessModal?.flag}
          handleClose={() => handleNextPuzzle()}
        />
      </View>
    </SafeAreaWrapper>
  );
}

export default ChessAnalysis;
